import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import React from "react";
import { PlayerDoubleMatch } from "../../interfaces";
import { Player } from "../../interfaces/Team";
import { LegCell, PlayerCell } from "../Cells";
import PlayerSelect from "../PlayerSelect";
import LegInputs from "./LegInputs";
import { useMatchDetailsContext } from "./MatchDetailsContext";

const ROW_HEIGHT = 40;
const DOUBLES_ROW_HEIGHT = ROW_HEIGHT * 2;

interface DoublesTableBodyProps {
  playerDoubleMatches: PlayerDoubleMatch[];
  playerMatchesLength: number;
  homePlayers: Player[];
  awayPlayers: Player[];
  homeInputs: { [matchId: number]: string[] };
  awayInputs: { [matchId: number]: string[] };
  handleDoublePlayerChange: (
    matchId: number,
    type: "home1" | "home2" | "away1" | "away2",
    playerId: number
  ) => void;
  handleInputChange: (
    type: "home" | "away",
    matchId: number,
    idx: number,
    value: string
  ) => void;
  handleInputBlur: (
    type: "home" | "away",
    matchId: number,
    idx: number
  ) => void;
  usedHomeDoubleIds?: number[];
  usedAwayDoubleIds?: number[];
  hoveredRow: number | null;
  setHoveredRow: React.Dispatch<React.SetStateAction<number | null>>;
  isAdmin: boolean;
}

const DoublesTableBody: React.FC<DoublesTableBodyProps> = ({
  playerDoubleMatches,
  playerMatchesLength,
  homePlayers,
  awayPlayers,
  homeInputs,
  awayInputs,
  handleDoublePlayerChange,
  handleInputChange,
  handleInputBlur,
  usedHomeDoubleIds,
  usedAwayDoubleIds,
  setHoveredRow,
  isAdmin,
}) => {
  const { mistakes } = useMatchDetailsContext();

  return (
    <TableBody>
      {playerDoubleMatches.map((dm, doubleIdx) => {
        const baseTab = playerMatchesLength * 6 + doubleIdx * 6;
        const homeVals = homeInputs[dm.id] || ["", "", ""];
        const awayVals = awayInputs[dm.id] || ["", "", ""];
        const winCountHome = homeVals.filter(
          (v) => v && !isNaN(Number(v))
        ).length;
        const winCountAway = awayVals.filter(
          (v) => v && !isNaN(Number(v))
        ).length;
        const mistake = mistakes.find((m) => m.id === dm.id);
        return (
          <React.Fragment key={dm.id}>
            <TableRow
              hover
              onMouseEnter={() => setHoveredRow(dm.id)}
              onMouseLeave={() =>
                setHoveredRow((prev) => (prev === dm.id ? null : prev))
              }
              sx={{
                borderLeft: mistake ? "8px solid red" : undefined,
              }}
            >
              <PlayerCell>
                <PlayerSelect
                  matchId={dm.id}
                  direction="ltr"
                  isAdmin={isAdmin}
                  players={homePlayers.map((p) => ({
                    ...p,
                    displayName:
                      p.name +
                      (dm.home_player_1 &&
                      p.id === dm.home_player_1.id &&
                      winCountHome > 0
                        ? ` (${winCountHome})`
                        : ""),
                  }))}
                  value={dm.home_player_1 ? dm.home_player_1.id : ""}
                  label={undefined}
                  placeholder="Heimspieler 1 wählen"
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    handleDoublePlayerChange(dm.id, "home1", newId);
                  }}
                  inputHeight={DOUBLES_ROW_HEIGHT / 2}
                  disabledOptions={
                    // Disable all players already used in other doubles, and also the other player in this double
                    [
                      ...(usedHomeDoubleIds || []).filter(
                        (id: number) =>
                          id !== dm.home_player_1?.id &&
                          id !== dm.home_player_2?.id
                      ),
                      dm.home_player_2?.id,
                    ].filter(Boolean) as number[]
                  }
                  matchType="doubles"
                />
                <PlayerSelect
                  matchId={dm.id}
                  direction="ltr"
                  isAdmin={isAdmin}
                  players={homePlayers.map((p) => ({
                    ...p,
                    displayName:
                      p.name +
                      (dm.home_player_2 &&
                      p.id === dm.home_player_2.id &&
                      winCountHome > 0
                        ? ` (${winCountHome})`
                        : ""),
                  }))}
                  value={dm.home_player_2 ? dm.home_player_2.id : ""}
                  label={undefined}
                  placeholder="Heimspieler 2 wählen"
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    handleDoublePlayerChange(dm.id, "home2", newId);
                  }}
                  inputHeight={DOUBLES_ROW_HEIGHT / 2}
                  disabledOptions={
                    [
                      ...(usedHomeDoubleIds || []).filter(
                        (id: number) =>
                          id !== dm.home_player_1?.id &&
                          id !== dm.home_player_2?.id
                      ),
                      dm.home_player_1?.id,
                    ].filter(Boolean) as number[]
                  }
                />
              </PlayerCell>

              <LegCell hasBorderRight>
                <LegInputs
                  isAdmin={isAdmin}
                  matchId={dm.id}
                  type="home"
                  values={homeVals}
                  onChange={(idx, value) =>
                    handleInputChange("home", dm.id, idx, value)
                  }
                  onBlur={(idx) => handleInputBlur("home", dm.id, idx)}
                  tabIndexBase={baseTab + 1}
                  idPrefix="home"
                  inputHeight={DOUBLES_ROW_HEIGHT}
                />
              </LegCell>
              <LegCell>
                <LegInputs
                  isAdmin={isAdmin}
                  matchId={dm.id}
                  type="away"
                  values={awayVals}
                  onChange={(idx, value) =>
                    handleInputChange("away", dm.id, idx, value)
                  }
                  onBlur={(idx) => handleInputBlur("away", dm.id, idx)}
                  tabIndexBase={baseTab + 4}
                  idPrefix="away"
                  inputHeight={DOUBLES_ROW_HEIGHT}
                />
              </LegCell>
              <PlayerCell>
                <PlayerSelect
                  matchId={dm.id}
                  direction="rtl"
                  isAdmin={isAdmin}
                  players={awayPlayers.map((p) => ({
                    ...p,
                    displayName:
                      p.name +
                      (dm.away_player_1 &&
                      p.id === dm.away_player_1.id &&
                      winCountAway > 0
                        ? ` (${winCountAway})`
                        : ""),
                  }))}
                  value={dm.away_player_1 ? dm.away_player_1.id : ""}
                  label={undefined}
                  placeholder="Auswärtsspieler 1 wählen"
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    handleDoublePlayerChange(dm.id, "away1", newId);
                  }}
                  inputHeight={DOUBLES_ROW_HEIGHT / 2}
                  disabledOptions={
                    [
                      ...(usedAwayDoubleIds || []).filter(
                        (id: number) =>
                          id !== dm.away_player_1?.id &&
                          id !== dm.away_player_2?.id
                      ),
                      dm.away_player_2?.id,
                    ].filter(Boolean) as number[]
                  }
                />
                <PlayerSelect
                  matchId={dm.id}
                  direction="rtl"
                  isAdmin={isAdmin}
                  players={awayPlayers.map((p) => ({
                    ...p,
                    displayName:
                      p.name +
                      (dm.away_player_2 &&
                      p.id === dm.away_player_2.id &&
                      winCountAway > 0
                        ? ` (${winCountAway})`
                        : ""),
                  }))}
                  value={dm.away_player_2 ? dm.away_player_2.id : ""}
                  label={undefined}
                  placeholder="Auswärtsspieler 2 wählen"
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    handleDoublePlayerChange(dm.id, "away2", newId);
                  }}
                  inputHeight={DOUBLES_ROW_HEIGHT / 2}
                  disabledOptions={
                    [
                      ...(usedAwayDoubleIds || []).filter(
                        (id: number) =>
                          id !== dm.away_player_1?.id &&
                          id !== dm.away_player_2?.id
                      ),
                      dm.away_player_1?.id,
                    ].filter(Boolean) as number[]
                  }
                />
              </PlayerCell>
            </TableRow>
          </React.Fragment>
        );
      })}
    </TableBody>
  );
};

export default DoublesTableBody;
